import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { SubmitHandler, useForm } from "react-hook-form";
import { TrackGoogleAnalyticsEvent } from "@/google-analytics";
import { useUserContext } from "@/contexts/userContext";
import { useToast } from "../ui/use-toast";
import MarketplacePopup from "@/sections/build/components/MarketplacePopup";
import { CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Tabs, TabsTrigger } from "../ui/tabs";
import { TabsList } from "@radix-ui/react-tabs";
import { BrokerName } from "@/helpers/types";
import { useRouter } from "next/router";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { joinValidString } from "@/helpers/helperFunctions";
import {
  useGetInstrumentSuggestion,
  useGetTrendingSymbols,
} from "@/api/symbolApi";
import { LuChevronsUpDown } from "react-icons/lu";
import { Separator } from "../ui/separator";
import { IoMdClose } from "react-icons/io";
import { FaAngleRight, FaCheck } from "react-icons/fa6";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";
import { BiSolidHelpCircle } from "react-icons/bi";
import { Input } from "../ui/input";
import Loader from "../Loader";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { TradingViewTimeFrames } from "../TradingViewWidgets";
import { useRecoilState } from "recoil";
import {
  selectedTimeFrameState,
  setSelectedSymbolState,
} from "@/Atoms/strategy/StrategyFormAtom";
import { usePostCreateStrategy } from "@/api/strategyApi";
import {
  brokerConnectModalAtom,
  guideModalAtom,
} from "@/Atoms/modal/DialogueAtoms";
import Fuse from "fuse.js";
import { useQuickStrategyContext } from "@/contexts/QuickStrategyContext";
import { enableVideoTutorial } from "@/config/logos";
import Link from "next/link";
interface IFormValues {
  symbol?: string;
  timeframe?: string;
  strategy_name?: string;
}
const isForex = true;
const CreateStrategyForm = () => {
  const buildForm = useForm<IFormValues>({ mode: "onSubmit" });
  const router = useRouter();
  const { user } = useUserContext();
  const { toast } = useToast();
  const { t } = useTranslation("build");
  const { t: ttt } = useTranslation("test");
  const [marketplacePopupShow, setMarketplacePopupShow] = useState(false);
  const [trendingSymbols, setTrendingSymbols] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const [strategyNameData, setStrategyNameData] = useState({
    sym: "",
    // dt: "",
    epoc: "",
    tf: "",
  });
  const [selectedTab, setSelectedTab] = useState(BrokerName.AP);
  const [showSuggestion, setShowSuggestion] = useState(false);

  const [suggestionSearch, setSuggestionSearch] = useState("");
  const [symbolLoading, setSymbolLoading] = useState(false);
  const [symbolData, setSymbolData] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const allSymbolsEnabledRef = useRef<boolean>(false);
  const [selectedSymbol, setSelectedSymbol] = useRecoilState(
    setSelectedSymbolState
  );
  const [selectedTimeFrame, setSelectedTimeFrame] = useRecoilState(
    selectedTimeFrameState
  );
  const [instrumentInput, setInstrumentInput] = useState("");
  const [filteredSymbolData, setFilteredSymbolData] = useState<
    { name: string; symbol: string; short_name: string }[]
  >([]);
  const {
    changeScannerType,
    setQuickStrategyModalToggle,
    isScannerType,
    openQuickStrategy,
  } = useQuickStrategyContext();
  const [showGuideModal, setShowGuideModal] = useRecoilState(guideModalAtom);

  const { subscriptionData, userType } = useSubscription();
  const {
    data: trendingSymbolsData,
    isSuccess: isTrendingSymbolSuccess,
    isLoading: isTrendingSymbolLoading,
  } = useGetTrendingSymbols("forex");
  const {
    data: createStrategyData,
    mutateAsync: createStrategy,
    isSuccess: isCreateStrategySuccess, // Indicates if the query was successful
    isPending: isCreateStrategyLoading, // Indicates if the query is currently loading
  } = usePostCreateStrategy(); // Fetch walkthrough status only if user ID exists

  const {
    data: instrumentSuggestionData,
    isSuccess: isInstrumentSuggestionSuccess,
    isLoading: isInstrumentSuggestionLoading,
  } = useGetInstrumentSuggestion(user?.user_id);

  const fuse = useMemo(() => {
    if (symbolData.length === 0) return null;

    return new Fuse(symbolData, {
      keys: ["name", "symbol", "short_name"],
      threshold: 0.4,
    });
  }, [symbolData]);

  const timeframeOptions = [
    {
      id: 0,
      label: t("container.item1.component.form.dropdown.option3"),
      value: "3m",
      tradingViewValue: TradingViewTimeFrames.THREE_MINUTE,
    },
    {
      id: 0,
      label: t("container.item1.component.form.dropdown.option4"),
      value: "5m",
      tradingViewValue: TradingViewTimeFrames.FIVE_MINUTES,
    },
    {
      id: 0,
      label: t("container.item1.component.form.dropdown.option5"),
      value: "15m",
      tradingViewValue: TradingViewTimeFrames.FIFTEEN_MINUTES,
    },
    {
      id: 0,
      label: t("container.item1.component.form.dropdown.option6"),
      value: "30m",
      tradingViewValue: TradingViewTimeFrames.THIRTY_MINUTES,
    },
    {
      id: 0,
      label: t("container.item1.component.form.dropdown.option7"),
      value: "60m",
      tradingViewValue: TradingViewTimeFrames.ONE_HOUR,
    },
  ];
  const updateStrategyNameData = useCallback(
    (
      field: keyof typeof strategyNameData,
      value: string | null | undefined
    ) => {
      const date = new Date();
      setStrategyNameData((p) => {
        const { sym, tf, epoc }: typeof strategyNameData = {
          ...p,
          [field]: value,
          epoc: Math.random().toString(36).substring(2, 7).toString(),
          // dt: formatDate(date, "DD.MM.YY"),
        };
        buildForm.setValue("strategy_name", joinValidString([sym, tf, epoc]));
        return { sym, tf, epoc };
      });
    },
    []
  );

  const clearFormSymbol = useCallback(() => {
    buildForm.setValue("symbol", undefined);
    updateStrategyNameData("sym", "");
  }, [updateStrategyNameData]);

  const onBuildFormSubmit: SubmitHandler<IFormValues> = async (
    values,
    event
  ) => {
    const { strategy_name, symbol, timeframe } =
      values as Required<IFormValues>;

    if (user) {
      const userId = user.user_id;

      try {
        await createStrategy({
          symbol: symbol,
          timeframe: timeframe,
          strategy_name: strategy_name,
          user_id: userId ?? "",
        });
      } catch (e) {
        console.warn(e);
        toast({
          title: "Error creating strategy",
          variant: "destructive",
        });
      }
    }
  };

  useEffect(() => {
    if (isTrendingSymbolSuccess && trendingSymbolsData) {
      setTrendingSymbols(
        Object.entries(
          trendingSymbolsData.data as { [key: string]: string }
        ).map(([symbol, name], index: number) => {
          return { name, symbol, short_name: symbol };
        })
      );
    }
  }, [isTrendingSymbolSuccess, trendingSymbolsData, isTrendingSymbolLoading]);

  useEffect(() => {
    if (isInstrumentSuggestionSuccess && instrumentSuggestionData) {
      if (Array.isArray(instrumentSuggestionData.data)) {
        setSymbolData([...instrumentSuggestionData.data]);
      }
    }
  }, [
    isInstrumentSuggestionSuccess,
    instrumentSuggestionData,
    isInstrumentSuggestionLoading,
  ]);
  useEffect(() => {
    // Check if the user walkthrough data has successfully loaded
    if (isCreateStrategySuccess && createStrategyData) {
      if ("strategy" in createStrategyData?.data) {
        TrackGoogleAnalyticsEvent(
          "strategy_created",
          "strategy_created",
          "strategy_created"
        );
        let _url = new URL(window.location.href);
        let locale_url = "en";
        try {
          if (
            _url.searchParams.get("locale") != undefined &&
            typeof _url.searchParams.get("locale") != undefined
          ) {
            locale_url = _url.searchParams.get("locale") || locale_url;
          }
        } catch (e) {
          console.warn(e);
        }
        router.replace(
          "/strategy/" +
            createStrategyData.data.strategy.id +
            "/" +
            createStrategyData.data.strategy.keygen +
            "/false" +
            `?ref=${encodeURIComponent(_url.pathname + _url.search)}&locale=` +
            locale_url +
            `&scanner=` +
            isScannerType
        );
      } else {
        toast({
          title: createStrategyData.data?.detail,
          variant: "destructive",
        });
      }
    }
  }, [isCreateStrategyLoading, isCreateStrategySuccess]); // Runs when either isUserWalkthroughSuccess or userWalkthroughData changes

  useEffect(() => {
    if (userType) {
      setSelectedTab(BrokerName.AP);
    }
  }, [userType]);

  useEffect(() => {
    allSymbolsEnabledRef.current = true;
    setSymbolData([]);
    updateStrategyNameData("sym", "");
  }, [subscriptionData, userType]);

  useEffect(() => {
    if (!fuse) return;

    if (instrumentInput.trim()) {
      const results = fuse.search(instrumentInput);
      setFilteredSymbolData(results.map((res) => res.item));
    } else {
      setFilteredSymbolData(symbolData);
    }
  }, [instrumentInput, fuse, symbolData]);

  return (
    <Form {...buildForm}>
      <form
        method="post"
        onSubmit={buildForm.handleSubmit(onBuildFormSubmit)}
        id="strategy-form-1"
        className="relative"
      >
        {marketplacePopupShow && strategyNameData?.sym && (
          <div className="absolute top-[10%] -right-[65%] mt-2 w-64 z-20">
            <MarketplacePopup
              show={marketplacePopupShow}
              setShow={setMarketplacePopupShow}
              symbol={strategyNameData?.sym}
            />
          </div>
        )}

        <CardHeader>
          <CardTitle className="flex">
            <div className="flex gap-2 items-center text-2xl font-bold tracking-tight">
              {isScannerType ? t("createNewScanner") : t("createNewStrategy")}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className={""}>
          <div className="flex flex-col mt-0 gap-5 cursor-pointer">
            <FormField
              control={buildForm.control}
              name="symbol"
              defaultValue={selectedSymbol}
              rules={{ required: t("container.item1.component.form.error1") }}
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>
                    {t("container.item1.component.form.label1")}
                  </FormLabel>

                  <Popover
                    open={showSuggestion}
                    onOpenChange={(open) => setShowSuggestion(open)}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          className={cn(
                            "justify-between text-[--marketplace-blue-new] border-[--marketplace-blue-new]"
                          )}
                        >
                          <div className="flex grow">
                            {selectedSymbol
                              ? symbolData
                                  .concat(trendingSymbols)
                                  .find(
                                    (item) => item.symbol === selectedSymbol
                                  )?.symbol ||
                                clearFormSymbol() ||
                                ""
                              : t("searchAsset")}
                          </div>
                          <div className="flex flex-row items-center">
                            {field.value ? (
                              <>
                                {" "}
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    clearFormSymbol();
                                    setShowSuggestion(false);
                                    setMarketplacePopupShow(false);
                                    setSymbolData([]);
                                    setSelectedSymbol(undefined);
                                  }}
                                >
                                  <IoMdClose />
                                </Button>
                                <Separator orientation="vertical" />
                              </>
                            ) : (
                              <></>
                            )}
                            <LuChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </div>
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0 cursor-pointer">
                      <div>
                        <input
                          className="flex-1 bg-transparent outline-none text-xs w-full p-3 border"
                          onChange={(srch) => {
                            allSymbolsEnabledRef.current &&
                              setInstrumentInput(srch.target.value);
                            setSuggestionSearch(srch.target.value);
                          }}
                          placeholder={
                            selectedTab == BrokerName.Blofin
                              ? t("container.item1.component.form.placeholder1")
                              : "Example: Start typing EURGBP..."
                          }
                        />

                        <div className="w-screen lg:max-w-sm cursor-pointer">
                          <div
                            className={`max-h-72 h-screen w-full flex flex-col rounded-md border overflow-y-auto pr-4`}
                          >
                            {suggestionSearch ? (
                              filteredSymbolData.length > 0 ? (
                                filteredSymbolData.map((item, index) => {
                                  let name =
                                    item.name.length > 50
                                      ? item.name.slice(0, 50) + "..."
                                      : item.name;
                                  return (
                                    <div
                                      // value={item.symbol + " " + item.name}
                                      key={item.symbol + "_" + index}
                                      onClick={() => {
                                        buildForm.setValue(
                                          "symbol",
                                          item.symbol
                                        );
                                        setSelectedSymbol(item.symbol);
                                        // getTvSymbol(item.symbol);

                                        updateStrategyNameData(
                                          "sym",
                                          item.symbol
                                        );
                                        setShowSuggestion(false);
                                        setMarketplacePopupShow(true);
                                      }}
                                    >
                                      <div className="flex flex-row gap-2 w-full items-center hover:bg-[hsl(var(--background))] rounded-sm">
                                        <div className="">
                                          <FaCheck
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              item.symbol === field.value
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                        </div>
                                        <div className="flex flex-col text-sm cursor-pointer w-full pt-2 pb-2">
                                          <div>{item.symbol}</div>
                                          <Separator />
                                          <div
                                            title={item.name}
                                            className="text-muted-foreground text-xs"
                                          >
                                            {name}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : (
                                <div className="w-full flex justify-center p-4">
                                  No symbol found
                                </div>
                              )
                            ) : trendingSymbols.length ? (
                              <>
                                <div className="text-center p-4 text-sm text-center">
                                  {t("Trending assets")}
                                </div>

                                <div className="border-t my-1"></div>
                                {trendingSymbols.map((item, index) => {
                                  let name =
                                    item.name.length > 50
                                      ? item.name.slice(0, 50) + "..."
                                      : item.name;
                                  return (
                                    <div
                                      key={item.symbol + "_" + index}
                                      onClick={() => {
                                        buildForm.setValue(
                                          "symbol",
                                          item.symbol
                                        );
                                        updateStrategyNameData(
                                          "sym",
                                          item.symbol
                                        );
                                        setSelectedSymbol(item.symbol);
                                        setShowSuggestion(false);
                                        setMarketplacePopupShow(true);
                                      }}
                                    >
                                      <div className="flex flex-row gap-2 w-full items-center hover:bg-[hsl(var(--background))] rounded-sm">
                                        <div className="">
                                          <FaCheck
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              item.symbol === field.value
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                        </div>
                                        <div className="flex flex-col text-sm cursor-pointer w-full pt-2 pb-2">
                                          <div>{item.symbol}</div>
                                          <Separator className="block hover:hidden" />
                                          <div
                                            title={item.name}
                                            className="text-muted-foreground text-xs truncate pr-4"
                                          >
                                            {name}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={buildForm.control}
              name="timeframe"
              rules={{ required: t("container.item1.component.form.error2") }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("container.item1.component.form.label2")}
                  </FormLabel>
                  <Select
                    onValueChange={(select?: string) => {
                      const _value = timeframeOptions.find(
                        (inst) => inst.value === select
                      );
                      _value && setSelectedTimeFrame(_value);

                      // Correctly update form state
                      field.onChange(select || undefined);

                      updateStrategyNameData("tf", select);
                    }}
                    defaultValue={field.value}
                    name="timeframe"
                    value={selectedTimeFrame?.value}
                  >
                    <FormControl>
                      <SelectTrigger className="text-[--marketplace-blue-new] border-[--marketplace-blue-new]">
                        <SelectValue
                          placeholder={t(
                            "container.item1.component.form.dropdown.option1"
                          )}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {timeframeOptions.map((option, index) => (
                        <SelectItem
                          value={option.value}
                          key={`option-${index}`}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={buildForm.control}
              name="strategy_name"
              rules={{ required: t("container.item1.component.form.error3") }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    <div className="flex relative gap-1">
                      <div>{t("container.item1.component.form.label3")}</div>
                      <div className="cursor-pointer">
                        <HoverCard openDelay={100}>
                          <HoverCardTrigger>
                            <BiSolidHelpCircle size={12} />
                          </HoverCardTrigger>
                          <HoverCardContent className="text-xs">
                            {t(
                              "container.item1.component.form.strategyAutoGeneratedDisabledInput"
                            )}
                          </HoverCardContent>
                        </HoverCard>
                      </div>
                    </div>
                  </FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="off"
                      placeholder={t(
                        "container.item1.component.form.placeholder3"
                      )}
                      {...field}
                      disabled
                      onChange={() => null}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {enableVideoTutorial && (
            <div className="text-sm mt-5 mb-1 flex gap-1 items-center">
              <div className="text-muted-foreground">
                {t("Not sure how to create a strategy?")}{" "}
              </div>

              <div
                className="hover:underline cursor-pointer"
                onClick={() => {
                  setShowGuideModal({
                    isOpen: true,
                    link: "https://www.youtube.com/embed/9PQaKeFyV_A",
                  });
                }}
              >
                {t("Click here")}
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex items-center   flex-col  ">
          <div className="w-full flex justify-end">
            <Button
              type="submit"
              className="flex gap-2"
              disabled={isCreateStrategyLoading}
            >
              {t("container.item1.button")}
              {isCreateStrategyLoading ? <Loader /> : <FaAngleRight />}
            </Button>
          </div>
          <div className="flex mt-5 text-[--marketplace-blue] font-bold  items-center justify-around border-t-2 pt-2 w-full  ">
            <div className=" flex cursor-pointer flex-col justify-end items-end ">
              <Link href={"/test"}>
                <div>
                  <p className="">View created </p>
                  <p>strategies</p>
                </div>
              </Link>
            </div>
            <div className="w-px bg-muted-foreground/25 h-12 self-center"></div>{" "}
            {/* Vertical Line */}
            <div className=" flex cursor-pointer  flex-col justify-end items-end ">
              <Link href={"/deploy"}>
                <div>
                  <p className="">View deployed </p>
                  <p>strategies</p>
                </div>
              </Link>
            </div>
          </div>
        </CardFooter>
      </form>
    </Form>
  );
};

export default CreateStrategyForm;
